import type {
  DashboardData,
  DashboardFiltersForm,
  DurationGroup,
  DurationMonthValue,
  NameValueItem,
  PlaceComparisonItem,
  SelectOption,
  StatusTableRow,
} from './types';

const MONTH_LABELS_LONG = ['ENERO', 'FEBRERO', 'MARZO', 'ABRIL', 'MAYO', 'JUNIO', 'JULIO', 'AGOSTO', 'SETIEMBRE', 'OCTUBRE', 'NOVIEMBRE', 'DICIEMBRE'];

const EMPTY_DASHBOARD_DATA: DashboardData = {
  attentionByPlace: { lima: [], provincia: [] },
  averageAmountByPlace: [],
  disbursementByStatus: [],
  agreementsToStart: [],
  financialInstitutionsToStart: [],
  agreementsExtort: [],
  financialInstitutionsExtort: [],
  totalAmountExtortByAgreement: [],
  totalAmountExtortByFinancialInstitution: [],
  totalApplicationsAndEffectivityByStatus: {
    months: [],
    rows: [],
    totalRow: null,
  },
  dashboardSummary: {
    months: [],
    groups: [],
    totalRow: [],
  },
};

type AverageAmountByPlaceResponseItem = {
  mes: string;
  plaza: 'LIMA' | 'PROVINCIA' | string;
  promedio_dias: number;
};

const normalizeText = (value: any): string => String(value ?? '').trim();

const toNumber = (value: any): number => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (typeof value === 'string') {
    const normalizedValue = value.replace(/,/g, '').trim();
    const parsedValue = Number(normalizedValue);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
  }
  return 0;
};

const findArrayInObject = (value: any, preferredKeys: string[] = []): any[] => {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== 'object') return [];

  const matchedArrayKey = preferredKeys.find((key) => Array.isArray(value[key]));
  if (matchedArrayKey) return value[matchedArrayKey];

  const firstArray = Object.values(value).find((item) => Array.isArray(item));
  return Array.isArray(firstArray) ? firstArray : [];
};

const findObjectInObject = (value: any, preferredKeys: string[] = []) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

  const matchedObjectKey = preferredKeys.find((key) => value[key] && typeof value[key] === 'object' && !Array.isArray(value[key]));
  if (matchedObjectKey) return value[matchedObjectKey];

  return value;
};

const getItemLabel = (item: any): string => {
  if (typeof item === 'string') return item;

  return normalizeText(
    item?.label
    ?? item?.name
    ?? item?.nombre
    ?? item?.title
    ?? item?.titulo
    ?? item?.descripcion
    ?? item?.description
    ?? item?.agreement
    ?? item?.convenio
    ?? item?.financialInstitution
    ?? item?.entidad
    ?? item?.place
    ?? item?.plaza
    ?? item?.status
    ?? item?.estado
    ?? item?.month
    ?? item?.mes,
  );
};

const getItemValue = (item: any, fallbackKeys: string[] = []) => {
  if (typeof item !== 'object' || item === null) return item;

  const keys = [
    ...fallbackKeys,
    'value',
    'cantidad',
    'count',
    'total',
    'monto',
    'amount',
    'average',
    'promedio',
    'efectividad',
    'effectiveness',
    'porcentaje',
  ];

  const keyFound = keys.find((key) => item[key] !== undefined && item[key] !== null);
  return keyFound ? item[keyFound] : 0;
};

const normalizePlace = (value: any) => {
  const normalizedValue = normalizeText(value).toUpperCase();
  return normalizedValue.includes('LIMA') ? 'LIMA' : 'PROVINCIA';
};

export const getInitialDashboardData = (): DashboardData => ({
  ...EMPTY_DASHBOARD_DATA,
  attentionByPlace: { lima: [], provincia: [] },
  totalApplicationsAndEffectivityByStatus: { months: [], rows: [], totalRow: null },
  dashboardSummary: { months: [], groups: [], totalRow: [] },
});

export const getDateString = (dateParam: Date): string => {
  const month = dateParam.getMonth() + 1;
  const day = dateParam.getDate();
  const monthString = month > 9 ? `${month}` : `0${month}`;
  const dayString = day > 9 ? `${day}` : `0${day}`;
  return `${dateParam.getFullYear()}-${monthString}-${dayString}`;
};

export const buildMonthOptions = (): SelectOption[] => Array.from({ length: 12 }, (_, index) => {
  const date = new Date();
  date.setMonth(date.getMonth() - index);
  return {
    value: `${date.getFullYear()}-${date.getMonth() + 1}`,
    label: `${MONTH_LABELS_LONG[date.getMonth()]} ${date.getFullYear()}`,
  };
});

export const getStaticPaymentChannelOptions = (): SelectOption[] => [
  { value: 'PROSEGUR', label: 'PROSEGUR' },
  { value: 'EVERIS', label: 'EVERIS' },
  { value: 'OFICINA', label: 'OFICINA' },
  { value: 'COMCORP', label: 'COMCORP' },
  { value: 'CANAL DE VENTA', label: 'CANAL DE VENTA' },
  { value: 'GESTOR COMERCIAL', label: 'GESTOR COMERCIAL' },
];

export const getStaticPlaceOptions = (): SelectOption[] => [
  { value: 'LIMA', label: 'LIMA' },
  { value: 'PROVINCIA', label: 'PROVINCIA' },
];

export const normalizeOptions = (response: any): SelectOption[] => {
  const data = findArrayInObject(response, ['data', 'rows', 'items', 'result']);

  return data
    .map((item) => {
      if (typeof item === 'string') {
        return { value: item, label: item };
      }

      const value = normalizeText(item?.id ?? item.value);
      const label = getItemLabel(item.nombre_comercial ?? item.label);

      if (!value && !label) return null;
      return {
        value: value || label,
        label: label || value,
      };
    })
    .filter(Boolean) as SelectOption[];
};

export const buildDashboardPayload = (formFilter: DashboardFiltersForm) => {
  const payload: Record<string, string> = {
    fechaInicio: getDateString(formFilter.startDate),
    fechaFin: getDateString(formFilter.endDate),
  };

  if (formFilter.convenio) payload.convenio = formFilter.convenio;
  if (formFilter.plaza) payload.plaza = formFilter.plaza;
  if (formFilter.fuvexEmpresa) payload.fuvexEmpresa = formFilter.fuvexEmpresa;
  if (formFilter.equipo) payload.equipo = formFilter.equipo;
  if (formFilter.entidad) payload.entidad = formFilter.entidad;
  if (formFilter.canalPago) payload.canalDePago = formFilter.canalPago;

  return payload;
};

export const normalizeNameValueSeries = (response: any, valueKeys: string[] = []): NameValueItem[] => {
  const sourceArray = findArrayInObject(response, ['data', 'rows', 'items', 'result']);

  return sourceArray
    .map((item) => ({
      name: getItemLabel(item),
      value: toNumber(getItemValue(item, valueKeys)),
    }))
    .filter((item) => item.name)
    .sort((firstItem, secondItem) => secondItem.value - firstItem.value);
};

export const normalizeAttentionByPlace = (response: any) => {
  const objectValue = findObjectInObject(response, ['data', 'result']);

  if (objectValue?.lima || objectValue?.provincia) {
    return {
      lima: normalizeNameValueSeries(objectValue?.lima || [], ['percentage', 'porcentaje']),
      provincia: normalizeNameValueSeries(objectValue?.provincia || [], ['percentage', 'porcentaje']),
    };
  }

  const sourceArray = findArrayInObject(response, ['data', 'rows', 'items', 'result']);
  const lima: NameValueItem[] = [];
  const provincia: NameValueItem[] = [];

  sourceArray.forEach((item) => {
    const normalizedItem = {
      name: getItemLabel(item),
      value: toNumber(getItemValue(item, ['percentage', 'porcentaje', 'value'])),
    };

    if (!normalizedItem.name) return;

    if (normalizePlace(item?.plaza ?? item?.place ?? item?.tipo ?? item?.grupo) === 'LIMA') {
      lima.push(normalizedItem);
      return;
    }

    provincia.push(normalizedItem);
  });

  return { lima, provincia };
};

export const normalizePlaceComparison = (
  data: Record<string, AverageAmountByPlaceResponseItem> | AverageAmountByPlaceResponseItem[]
): PlaceComparisonItem[] => {
  const items = Array.isArray(data) ? data : Object.values(data);

  const grouped = items.reduce<Record<string, PlaceComparisonItem>>((acc, item) => {
    const { mes } = item;

    if (!acc[mes]) {
      acc[mes] = {
        name: mes,
        lima: 0,
        provincia: 0,
      };
    }

    if (item.plaza === 'LIMA') {
      acc[mes].lima = Number(item.promedio_dias);
    }

    if (item.plaza === 'PROVINCIA') {
      acc[mes].provincia = Number(item.promedio_dias);
    }

    return acc;
  }, {});

  return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
};

export const getErrorMessage = (error: any) => {
  if (!error) return 'No se pudo completar la consulta.';
  if (typeof error === 'string') return error;
  return error?.message || 'No se pudo completar la consulta.';
};

// #################################################
type RawItem = {
  institucion_financiera: string;
  plaza: string;
  atenciones: number;
};

export function transformAttentionByEntityData(
  data: Record<string, RawItem>
): {
    lima: NameValueItem[];
    provincia: NameValueItem[];
  } {
  const values = Object.values(data);

  const lima: NameValueItem[] = [];
  const provincia: NameValueItem[] = [];

  values.forEach((item) => {
    const mapped = {
      name: item.institucion_financiera,
      value: item.atenciones,
    };
    if (item.plaza === 'LIMA') {
      lima.push(mapped);
    } else if (item.plaza === 'PROVINCIA') {
      provincia.push(mapped);
    }
  });
  return { lima, provincia };
}

export function transformAndSort(data) {
  const orderMap = {
    bajo: { order: 1, label: '1) BAJA (0-5 DIAS)' },
    medio: { order: 2, label: '2) MEDIA (6-10 DIAS)' },
    alerta: { order: 3, label: '3) ALERTA (+10 DIAS)' },
  };

  const data2 = Object.values(data)
    .map((item: any) => ({
      name: orderMap[item.estado]?.label ?? item.estado,
      value: item.cantidad,
      order: orderMap[item.estado]?.order ?? 999,
    }))
    .sort((a, b) => a.order - b.order)
    .map(({ name, value }) => ({ name, value }));
  return data2;
}

// #####################3

type RawDashboardSummaryItem = {
  mes: string;
  estado: string;
  estado_dias: string;
  cantidad: number;
  importe: number;
};

type DurationDetail = {
  stateDays: string;
  values: DurationMonthValue[];
};

const DASHBOARD_STATE_DAYS_ORDER = [
  '3) ALERTA (>10 DIAS)',
  '2) MEDIA (6-10 DIAS)',
  '1) BAJA (0-5 DIAS)',
];

const formatNumberCell = (value: number): string => {
  if (!value) return '-';
  return value.toLocaleString('es-PE');
};

export const normalizeDashboardSummary = (
  response: RawDashboardSummaryItem[],
): {
  months: string[];
  groups: DurationGroup[];
  totalRow: DurationMonthValue[];
} => {
  const items = Array.isArray(response) ? response : [];

  const months = Array.from(new Set(items.map((item) => item.mes)));

  const states = Array.from(new Set(items.map((item) => item.estado)));

  const groups: DurationGroup[] = states.map((state) => {
    const stateItems = items.filter((item) => item.estado === state);

    const stateDaysList = Array.from(new Set(stateItems.map((item) => item.estado_dias))).sort(
      (first, second) => {
        const firstIndex = DASHBOARD_STATE_DAYS_ORDER.indexOf(first);
        const secondIndex = DASHBOARD_STATE_DAYS_ORDER.indexOf(second);

        const safeFirst = firstIndex === -1 ? 999 : firstIndex;
        const safeSecond = secondIndex === -1 ? 999 : secondIndex;

        return safeFirst - safeSecond;
      },
    );

    const details: DurationDetail[] = stateDaysList.map((stateDays) => {
      const values: DurationMonthValue[] = months.map((month) => {
        const found = stateItems.find(
          (item) => item.estado_dias === stateDays && item.mes === month,
        );

        return {
          quantity: formatNumberCell(found?.cantidad ?? 0),
          amount: formatNumberCell(found?.importe ?? 0),
        };
      });

      return {
        stateDays,
        values,
      };
    });

    const subtotal: DurationMonthValue[] = months.map((month) => {
      const monthItems = stateItems.filter((item) => item.mes === month);

      const quantity = monthItems.reduce((sum, item) => sum + (item.cantidad || 0), 0);
      const amount = monthItems.reduce((sum, item) => sum + (item.importe || 0), 0);

      return {
        quantity: formatNumberCell(quantity),
        amount: formatNumberCell(amount),
      };
    });

    return {
      state,
      details,
      subtotal,
    };
  });

  const totalRow: DurationMonthValue[] = months.map((month) => {
    const monthItems = items.filter((item) => item.mes === month);

    const quantity = monthItems.reduce((sum, item) => sum + (item.cantidad || 0), 0);
    const amount = monthItems.reduce((sum, item) => sum + (item.importe || 0), 0);

    return {
      quantity: formatNumberCell(quantity),
      amount: formatNumberCell(amount),
    };
  });

  return {
    months,
    groups,
    totalRow,
  };
};

type RawStatusEffectivityItem = {
  mes: string;
  estado: string;
  cantidad: number;
  efectividad: number;
};

const formatQuantityCell = (value: number): string => {
  if (!value) return '-';
  return value.toLocaleString('es-PE');
};

const formatEffectivenessCell = (value: number): string => {
  if (!value) return '-';
  return `${value}%`;
};

export const normalizeStatusEffectivityTable = (
  response: Record<string, RawStatusEffectivityItem> | RawStatusEffectivityItem[],
): {
  months: string[];
  rows: StatusTableRow[];
  totalRow: StatusTableRow | null;
} => {
  const items = Array.isArray(response) ? response : Object.values(response ?? {});

  if (items.length === 0) {
    return {
      months: [],
      rows: [],
      totalRow: null,
    };
  }

  const months = Array.from(new Set(items.map((item) => item.mes)));

  const statuses = Array.from(new Set(items.map((item) => item.estado)));

  const rows: StatusTableRow[] = statuses.map((status) => ({
    status,
    values: months.map((month) => {
      const found = items.find(
        (item) => item.estado === status && item.mes === month,
      );

      return {
        quantity: formatQuantityCell(found?.cantidad ?? 0),
        effectiveness: formatEffectivenessCell(found?.efectividad ?? 0),
      };
    }),
  }));

  const totalRow: StatusTableRow = {
    status: 'Total',
    values: months.map((month) => {
      const monthItems = items.filter((item) => item.mes === month);

      const totalQuantity = monthItems.reduce(
        (acc, item) => acc + (item.cantidad || 0),
        0,
      );

      return {
        quantity: formatQuantityCell(totalQuantity),
        effectiveness: formatEffectivenessCell(100) /* formatEffectivenessCell(Number(weightedEffectiveness.toFixed(2))) */,
      };
    }),
  };

  return {
    months,
    rows,
    totalRow,
  };
};
